import React, { useState, useEffect } from "react";
import { Star, ChevronLeft, ChevronRight, User } from "lucide-react";

const Testimonials = ({ testimonials = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // ✅ Dynamic slides to show based on screen width
  const getSlidesToShow = () => {
    if (window.innerWidth < 640) return 1; // mobile
    if (window.innerWidth < 1024) return 2; // tablet
    return 3; // desktop
  };

  const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow());

  useEffect(() => {
    const handleResize = () => setSlidesToShow(getSlidesToShow());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const renderStars = (count) => (
    <div className="flex gap-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          size={16}
          className={`${i < count ? "text-green-500 fill-green-500" : "text-gray-300"}`}
        />
      ))}
    </div>
  );

  const nextSlide = () => {
    setCurrentIndex((prev) =>
      prev === testimonials.length - slidesToShow ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? testimonials.length - slidesToShow : prev - 1
    );
  };

  // ✅ Auto slide
  useEffect(() => {
    const id = setInterval(nextSlide, 5000);
    return () => clearInterval(id);
  }, [slidesToShow, testimonials]);

  if (!testimonials.length) return null;

  return (
    <section
      id="testimonials"
      className="bg-gradient-to-b from-white to-blue-50 py-12 md:py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
        {/* Heading */}
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-blue-700 mb-2">
          What Clients Say
        </h2>
        <p className="text-gray-600 mb-8 md:mb-12 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
          Families trust HPR Infra for transparent processes and quality construction.
        </p>

        {/* Slider container */}
        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-in-out"
            style={{
              transform: `translateX(-${currentIndex * (100 / slidesToShow)}%)`,
            }}
          >
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="w-full sm:w-1/2 lg:w-1/3 shrink-0 px-2 sm:px-3 flex justify-center"
              >
                <div className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 p-6 text-left h-full flex flex-col justify-between border border-gray-100 max-w-[340px] sm:max-w-full relative">
                  
                  {/* ✅ Responsive image positioned absolutely on top-right */}
                  <div className="absolute top-4 right-4 sm:top-5 sm:right-5 md:top-6 md:right-6">
                    {t.image ? (
                      <img
                        src={t.image}
                        alt={t.name}
                        className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full object-cover border-2 border-blue-200 shadow-sm"
                      />
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center border-2 border-blue-200 shadow-sm">
                        <User className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-blue-400" />
                      </div>
                    )}
                  </div>

                  {/* Original content - unchanged */}
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-blue-700 mb-1 pr-16 sm:pr-18 md:pr-20">
                      {t.name}
                    </h4>
                    <div className="flex items-center gap-2 text-xs sm:text-sm text-green-600 mb-3">
                      {renderStars(t.rating || 0)}
                      <span className="text-gray-500">— {t.time || "recently"}</span>
                    </div>

                    {/* ✅ Responsive text truncation - original */}
                    <p
                      className="
                        text-gray-700 
                        text-sm sm:text-base 
                        leading-relaxed
                        line-clamp-5 sm:line-clamp-4 md:line-clamp-none
                        overflow-hidden sm:overflow-visible
                        text-justify
                      "
                    >
                      "{t.text}"
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation buttons (hidden on mobile) */}
          <button
            onClick={prevSlide}
            className="hidden sm:flex absolute left-2 md:left-0 top-1/2 -translate-y-1/2 bg-white rounded-full shadow-md p-2 md:p-3 border border-gray-200 hover:shadow-lg transition-all"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>

          <button
            onClick={nextSlide}
            className="hidden sm:flex absolute right-2 md:right-0 top-1/2 -translate-y-1/2 bg-white rounded-full shadow-md p-2 md:p-3 border border-gray-200 hover:shadow-lg transition-all"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Pagination dots */}
        <div className="mt-6 flex justify-center gap-2 flex-wrap">
          {Array.from({ length: Math.max(1, testimonials.length - slidesToShow + 1) }).map(
            (_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-2.5 w-2.5 rounded-full ${
                  currentIndex === i ? "bg-blue-600 w-6" : "bg-gray-300"
                } transition-all`}
              />
            )
          )}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;